import React, { useContext, useMemo, useState } from 'react';
import { CheckIcon, CloseIcon } from 'react-line-awesome';
import { useStyletron } from 'baseui';
import { Breadcrumbs } from 'baseui/breadcrumbs';
import { Input } from 'baseui/input';
import { StyledLink as Link } from 'baseui/link';
import { nanoid } from 'nanoid';
import { Button } from '@gtintel/platform-atoms';
import { generateZip } from '@gtintel/platform-utils';
import { FolderStructure } from './FolderStructure';
import { DocumentsContext, DocumentsParentContext } from './contexts';
import { combineBranches, findFolderById, generateTreeNodeFromPath, splitFilepathIntoTree } from './utils';
const DocumentList = ({
  selectedFolder,
  setSelectedFolder,
  parentFolders,
  setParentFolders,
  canUpload
}) => {
  const {
    name
  } = useContext(DocumentsParentContext);
  const {
    documents,
    documentTree,
    buildDocumentTree,
    removeAttachment
  } = useContext(DocumentsContext);
  const [checked, setChecked] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const allDocuments = documents();
  const openFolder = node => {
    setParentFolders([...node.parents, {
      name: node.name,
      id: node.id
    }]);
    setSelectedFolder(node);
  };
  const openHome = () => {
    setParentFolders([]);
    setSelectedFolder(undefined);
  };
  const addFolder = () => {
    const tree = documentTree();
    const folderPath = parentFolders.map(folder => folder.name);
    folderPath.push(newFolderName);
    const node = generateTreeNodeFromPath(folderPath);
    combineBranches(tree, node);
    buildDocumentTree(tree);
  };
  const createFolder = () => {
    addFolder();
    setNewFolderName('');
    setCreatingFolder(false);
  };
  const selectAllDocuments = () => {
    setChecked(allDocuments);
  };
  useMemo(() => {
    const tree = [];
    // TODO: see if setting as a Map of Maps is better
    try {
      const branches = [];
      allDocuments.forEach(document => {
        branches.push(splitFilepathIntoTree(document));
      });
      branches.forEach(node => {
        combineBranches(tree, node);
      });
      buildDocumentTree(tree);
    } catch (e) {
      console.error(e);
    }
  }, [allDocuments.length]);
  return <DocumentListBody canCreateFolder={canUpload} creatingFolder={creatingFolder} setCreatingFolder={setCreatingFolder} newFolderName={newFolderName} setNewFolderName={setNewFolderName} createFolder={createFolder} openFolder={openFolder} openHome={openHome} parentFolders={parentFolders} documentTree={documentTree} selectedFolder={selectedFolder} checked={checked} setChecked={setChecked} name={name()} selectAllDocuments={selectAllDocuments} removeAttachment={removeAttachment} />;
};
const CreateFolder = ({
  creatingFolder,
  setCreatingFolder,
  newFolderName,
  setNewFolderName,
  createFolder
}) => {
  return creatingFolder ? <React.Fragment>
      <Input overrides={{
      Input: {
        props: {
          'data-testid': 'create-folder-input'
        }
      }
    }} size="mini" value={newFolderName} onChange={e => setNewFolderName(e.currentTarget.value)} onKeyPress={e => {
      if (e.key === 'Enter') {
        createFolder();
      }
    }} placeholder="Folder Name" autoFocus />
      <Button size="mini" kind="success" disabled={newFolderName.length <= 0} onClick={createFolder} testId="save-folder-button">
        <CheckIcon />
      </Button>
      <Button size="mini" kind="error" onClick={() => setCreatingFolder(false)} testId="cancel-create-folder-button">
        <CloseIcon />
      </Button>
    </React.Fragment> : <Button size="mini" kind="secondary" onClick={() => setCreatingFolder(!creatingFolder)} testId="create-folder-button">
      Create Folder
    </Button>;
};
export const DocumentListBody = ({
  createFolder,
  openFolder,
  openHome,
  parentFolders,
  documentTree,
  selectedFolder,
  checked,
  setChecked,
  newFolderName,
  setNewFolderName,
  creatingFolder,
  setCreatingFolder,
  name,
  selectAllDocuments,
  removeAttachment,
  canCreateFolder
}) => {
  const [css, theme] = useStyletron();
  return <section className={css({
    backgroundColor: theme.colors.backgroundPrimary,
    border: '2px solid #e2e2e2',
    borderRadius: '12px',
    margin: '32px 0',
    display: 'flex',
    flexFlow: 'column',
    overflow: 'hidden',
    width: '100%'
  })} data-testid="document-list">
      <div className={css({
      alignItems: 'center',
      background: '#e2e2e2',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px'
    })} data-testid="breadcrumbs">
        <Breadcrumbs>
          <Link data-testid="home-folder-link" onClick={openHome}>
            Documents
          </Link>
          {parentFolders.map((folder, index) => <Link onClick={() => openFolder(findFolderById(documentTree(), folder.id))} key={nanoid()}>
              {folder.name}
            </Link>)}
        </Breadcrumbs>
      </div>

      <FolderStructure data={selectedFolder ? findFolderById(documentTree(), selectedFolder.id).children : documentTree()} checked={checked} name={name} setChecked={setChecked} openFolder={openFolder} removeAttachment={removeAttachment} />

      <div className={css({
      alignItems: 'center',
      borderTop: '1px solid #eee',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '14px'
    })}>
        <div className={css({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row nowrap',
        gap: '8px'
      })}>
          {canCreateFolder && <CreateFolder createFolder={createFolder} creatingFolder={creatingFolder} setCreatingFolder={setCreatingFolder} newFolderName={newFolderName} setNewFolderName={setNewFolderName} />}
        </div>

        <div className={css({
        alignItems: 'center',
        display: 'flex',
        flexFlow: 'row nowrap',
        gap: '8px'
      })}>
          <Button testId="select-all-button" size="mini" kind="secondary" onClick={selectAllDocuments}>
            Select All
          </Button>
          <Button testId="clear-all-button" size="mini" kind="secondary" disabled={checked.length <= 0} onClick={() => setChecked([])}>
            Clear Selected
          </Button>
          <Button size="mini" kind="primary" disabled={checked.length <= 1} onClick={() => generateZip(checked, name)} testId="download-selected-button">
            <span>Download Selected ({checked.length})</span>
          </Button>
        </div>
      </div>
    </section>;
};
export default DocumentList;